/**
 * VOS Movimento · as receitas. Uma por PAPEL de superfície, montadas a partir
 * dos tokens e prontas pra espalhar num `m.div`:
 *
 *   const mov = useMovimento();
 *   <m.div {...mov.r.modal} />
 *
 * Duas entradas mudam tudo de uma vez:
 * - `escala` multiplica todo tempo (1 = normal; 2 = câmera lenta; 0 = sem
 *   movimento). É o que o laboratório usa pra inspecionar e o que a
 *   preferência "reduzir movimento" usa pra zerar deslocamento.
 * - `reduzido`: quem pediu menos movimento no sistema (ou no VOS) recebe só
 *   opacidade, curta. Nada anda, nada escala, nada quica, nada conta.
 */
import type { TargetAndTransition, Transition, Variants } from "motion/react";
import {
  ATRASO,
  CASCATA,
  CICLO,
  CURVA,
  DESLOC,
  DUR,
  DUR_APERTO,
  DUR_SAIDA,
  ESCALA,
  MOLA,
  atrasoDaCascata,
  type Degrau,
  type NomeDaMola,
} from "./tokens";

/** Lado em que o flutuante aparece em relação ao gatilho. */
export type Lado = "top" | "bottom" | "left" | "right";

/** Onde o flutuante se alinha ao gatilho no outro eixo (o `align` do Menu e do Popover). */
export type Alinhamento = "inicio" | "centro" | "fim";

/** O que uma receita entrega: props prontas pra um componente do Motion. */
export type Receita = {
  initial: TargetAndTransition | false;
  animate: TargetAndTransition;
  exit: TargetAndTransition;
  style?: Record<string, string | number>;
};

export type OpcoesDoMovimento = {
  escala?: number;
  reduzido?: boolean;
  /**
   * `false` enquanto a página hidrata: o que veio no HTML do servidor tem que
   * aparecer PRONTO. Uma receita com `initial` pintava `opacity: 0` no HTML, e
   * o conteúdo ficava invisível até o JS carregar e animar. Hidratou, tudo que
   * montar depois (dado chegando, navegação, abrir) entra pela receita.
   */
  hidratado?: boolean;
};

/**
 * A origem da transformação nasce no lado do gatilho E no canto em que o
 * painel se alinha a ele: o menu alinhado pelo início de um botão cresce do
 * canto de cima à esquerda, não do meio.
 */
function origem(lado: Lado, alinhamento: Alinhamento) {
  const eixo = alinhamento === "inicio" ? "0%" : alinhamento === "fim" ? "100%" : "50%";
  if (lado === "bottom") return `${eixo} 0%`;
  if (lado === "top") return `${eixo} 100%`;
  if (lado === "left") return `100% ${eixo}`;
  return `0% ${eixo}`;
}

function passo(lado: Lado, d: number) {
  // Painel embaixo do gatilho vem de cima (y negativo), e assim por diante.
  if (lado === "bottom") return { y: -d };
  if (lado === "top") return { y: d };
  if (lado === "left") return { x: d };
  return { x: -d };
}

export function criarMovimento({ escala = 1, reduzido = false, hidratado = true }: OpcoesDoMovimento = {}) {
  const k = Math.max(0, escala);

  /**
   * Tween de um degrau: entrada ou saída. `atraso` em segundos SEM escala (a
   * câmera lenta é aplicada aqui). Reduzido: vira o esmaecer curto, sem atraso.
   */
  const tempo = (degrau: Degrau, sentido: "entrada" | "saida" = "entrada", atraso = 0): Transition =>
    reduzido
      ? { duration: Math.min(DUR.xs, DUR[degrau]) * k, ease: [...CURVA.entrada], delay: 0 }
      : {
          duration: (sentido === "entrada" ? DUR[degrau] : DUR_SAIDA[degrau]) * k,
          ease: sentido === "entrada" ? [...CURVA.entrada] : [...CURVA.saida],
          /* Sem atraso, sem a chave: o Motion monta a transição do filho como
             `{ delay: orquestrado, ...doFilho }`, e um `delay: 0` explícito
             apagava o `delayChildren`/`staggerChildren` do pai (a cascata
             chegava toda junta). No reduzido o zero fica: lá não há cascata. */
          ...(atraso > 0 ? { delay: atraso * k } : {}),
        };

  /** Mola escalada. `k = 0` vira salto seco (duração zero). */
  const mola = (nome: NomeDaMola): Transition => {
    if (k === 0 || reduzido) return { duration: reduzido ? DUR.xs * k : 0 };
    const m = MOLA[nome];
    return { type: "spring", visualDuration: m.visualDuration * k, bounce: m.bounce };
  };

  /** Reduzido: tudo vira um esmaecer curto. */
  const esmaecer = (atraso = 0): Receita => ({
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: tempo("xs", "entrada", atraso) },
    exit: { opacity: 0, transition: tempo("xs", "saida") },
  });

  /* ── Sobreposições ─────────────────────────────────────────────── */

  /** Fundo escurecido: o único que mexe na opacidade atrás do painel. */
  const fundo = (degrau: Degrau = "md"): Receita => ({
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: tempo(degrau) },
    exit: { opacity: 0, transition: tempo(degrau, "saida") },
  });

  /** Modal: .97 → 1 com opacidade (founder 26/09). Sai encolhendo 2%. */
  const modal: Receita = reduzido
    ? esmaecer()
    : {
        initial: { opacity: 0, scale: ESCALA.modal },
        animate: { opacity: 1, scale: 1, transition: tempo("md") },
        exit: { opacity: 0, scale: ESCALA.saida, transition: tempo("md", "saida") },
      };

  /**
   * Gaveta e faixa lateral: entram de fora da tela e NÃO esmaecem (o fundo já
   * diz que algo cobriu a tela; esmaecer o painel duplicava a informação).
   */
  const gaveta = (lado: "right" | "left" = "right"): Receita =>
    reduzido
      ? esmaecer()
      : {
          initial: { x: lado === "right" ? "100%" : "-100%" },
          animate: { x: 0, transition: tempo("lg") },
          exit: { x: lado === "right" ? "100%" : "-100%", transition: tempo("lg", "saida") },
        };

  /** Folha de baixo (celular): sobe da borda. O arraste pra fechar usa a mola `gesto`. */
  const folha: Receita = reduzido
    ? esmaecer()
    : {
        initial: { y: "100%" },
        animate: { y: 0, transition: tempo("lg") },
        exit: { y: "100%", transition: tempo("lg", "saida") },
      };

  /**
   * O conteúdo da gaveta chega em blocos, logo depois do painel começar a
   * andar: o olho acompanha o painel e encontra o conteúdo pousando.
   */
  const conteudoProgressivo: { container: Variants; item: Variants } = {
    container: {
      fechado: {},
      aberto: {
        transition: reduzido
          ? {}
          : { delayChildren: ATRASO.conteudoDaGaveta * k, staggerChildren: CASCATA.passo * 2 * k },
      },
    },
    item: reduzido
      ? { fechado: { opacity: 0 }, aberto: { opacity: 1, transition: tempo("xs") } }
      : {
          fechado: { opacity: 0, y: DESLOC.conteudo },
          aberto: { opacity: 1, y: 0, transition: tempo("md") },
        },
  };

  /* ── Flutuantes ────────────────────────────────────────────────── */

  /**
   * Menu, popover, select, autocomplete, menu de contexto: 150ms, nascem 2%
   * menores e 4px pra dentro do gatilho, crescendo a partir dele.
   */
  const flutuante = (lado: Lado = "bottom", alinhamento: Alinhamento = "centro"): Receita =>
    reduzido
      ? esmaecer()
      : {
          initial: { opacity: 0, scale: ESCALA.flutuante, ...passo(lado, DESLOC.flutuante) },
          animate: { opacity: 1, scale: 1, x: 0, y: 0, transition: tempo("sm") },
          exit: { opacity: 0, scale: ESCALA.flutuante, transition: tempo("sm", "saida") },
          style: { transformOrigin: origem(lado, alinhamento) },
        };

  /** Tooltip: depois do atraso (`ATRASO.tooltip`), 150ms; some em 70. */
  const tooltip = (lado: Lado = "top"): Receita =>
    reduzido
      ? esmaecer()
      : {
          initial: { opacity: 0, scale: ESCALA.tooltip, ...passo(lado, DESLOC.flutuante) },
          animate: { opacity: 1, scale: 1, x: 0, y: 0, transition: tempo("sm") },
          exit: { opacity: 0, scale: ESCALA.tooltip, transition: tempo("xs", "saida") },
          style: { transformOrigin: origem(lado, "centro") },
        };

  /** Submenu: sai de lado, do item que o abriu, alinhado pelo topo dele. */
  const submenu = flutuante("right", "inicio");

  /* ── Conteúdo ──────────────────────────────────────────────────── */

  /** Página e módulo: o conteúdo novo sobe 8px e aparece (200ms). */
  const pagina: Receita = reduzido
    ? esmaecer()
    : {
        initial: { opacity: 0, y: DESLOC.conteudo },
        animate: { opacity: 1, y: 0, transition: tempo("md") },
        exit: { opacity: 0, transition: tempo("xs", "saida") },
      };

  /**
   * Vista com SENTIDO: aba vizinha, passo de wizard, lista → conversa, voltar
   * no histórico. `custom` é o sentido: +1 avança (entra pela direita), -1
   * volta. Deslocamento curto na aba (8), maior entre vistas (24).
   */
  const vista = (distancia: number = DESLOC.vista): Variants =>
    reduzido
      ? {
          entra: { opacity: 0 },
          fica: { opacity: 1, transition: tempo("xs") },
          sai: { opacity: 0, transition: tempo("xs", "saida") },
        }
      : {
          entra: (dir: number = 1) => ({ opacity: 0, x: dir * distancia }),
          fica: { opacity: 1, x: 0, transition: tempo("md") },
          sai: (dir: number = 1) => ({ opacity: 0, x: dir * -distancia, transition: tempo("md", "saida") }),
        };

  /** Linha, card, mensagem, item de lista: sobe 8px; sai encolhendo 2%. */
  const item = (i = 0): Receita =>
    reduzido
      ? esmaecer()
      : {
          initial: { opacity: 0, y: DESLOC.conteudo },
          animate: { opacity: 1, y: 0, transition: tempo("md", "entrada", atrasoDaCascata(i)) },
          exit: { opacity: 0, scale: ESCALA.saida, transition: tempo("sm", "saida") },
        };

  /** Chip, filtro, tag: cresce do lugar dele; os vizinhos abrem espaço pela mola `layout`. */
  const chip: Receita = reduzido
    ? esmaecer()
    : {
        initial: { opacity: 0, scale: ESCALA.chip },
        animate: { opacity: 1, scale: 1, transition: tempo("sm") },
        exit: { opacity: 0, scale: ESCALA.chip, transition: tempo("sm", "saida") },
      };

  /** Rótulo trocando no mesmo lugar (Salvar → Salvando → Salvo): a face que sai sobe e desfoca. */
  const rotulo: Receita = reduzido
    ? esmaecer()
    : {
        initial: { opacity: 0, y: 5, filter: "blur(2px)" },
        animate: { opacity: 1, y: 0, filter: "blur(0px)", transition: tempo("sm") },
        exit: { opacity: 0, y: -5, filter: "blur(2px)", transition: tempo("sm", "saida") },
      };

  /** Ícone que vira outro: encolhe girando e o novo cresce no lugar. */
  const icone = (girar = false): Receita =>
    reduzido
      ? esmaecer()
      : {
          initial: { opacity: 0, scale: 0.5, rotate: girar ? -90 : 0, filter: "blur(2px)" },
          animate: { opacity: 1, scale: 1, rotate: 0, filter: "blur(0px)", transition: tempo("sm") },
          exit: { opacity: 0, scale: 0.5, rotate: girar ? 90 : 0, filter: "blur(2px)", transition: tempo("sm", "saida") },
        };

  /** Altura automática: acordeão, campo condicional, erro de campo. */
  const recolher: Receita = reduzido
    ? {
        initial: { height: 0, opacity: 0 },
        animate: { height: "auto", opacity: 1, transition: { height: { duration: 0 }, opacity: tempo("xs") } },
        exit: { height: 0, opacity: 0, transition: { height: { duration: 0 }, opacity: tempo("xs", "saida") } },
      }
    : {
        initial: { height: 0, opacity: 0 },
        animate: {
          height: "auto",
          opacity: 1,
          transition: { height: tempo("md"), opacity: tempo("sm", "entrada", 0.05) },
        },
        exit: {
          height: 0,
          opacity: 0,
          transition: { height: tempo("md", "saida"), opacity: tempo("xs", "saida") },
        },
      };

  /** Esqueleto → conteúdo: o esqueleto esmaece, o conteúdo pousa 4px. */
  const chegada: Receita = reduzido
    ? esmaecer()
    : {
        initial: { opacity: 0, y: 4 },
        animate: { opacity: 1, y: 0, transition: tempo("md") },
        exit: { opacity: 0, transition: tempo("sm", "saida") },
      };

  /** Mensagem de conversa: chega do lado de quem falou, pela base do balão. */
  const mensagem = (deQuem: "cliente" | "equipe"): Receita =>
    reduzido
      ? esmaecer()
      : {
          initial: { opacity: 0, y: DESLOC.conteudo, x: deQuem === "cliente" ? -4 : 4, scale: ESCALA.saida },
          animate: { opacity: 1, y: 0, x: 0, scale: 1, transition: tempo("md") },
          exit: { opacity: 0, transition: tempo("xs", "saida") },
          style: { transformOrigin: deQuem === "cliente" ? "bottom left" : "bottom right" },
        };

  /** Toast: sobe da base; quem já estava na pilha se reorganiza pela mola `layout`. */
  const toast: Receita = reduzido
    ? esmaecer()
    : {
        initial: { opacity: 0, y: DESLOC.conteudo * 2, scale: ESCALA.saida },
        animate: { opacity: 1, y: 0, scale: 1, transition: tempo("md") },
        exit: { opacity: 0, y: DESLOC.conteudo, scale: ESCALA.saida, transition: tempo("md", "saida") },
      };

  /* ── Gesto ─────────────────────────────────────────────────────── */

  /** O botão afunda na hora (80ms) e volta na mola do gesto. */
  const aperto: TargetAndTransition = reduzido
    ? {}
    : { scale: ESCALA.aperto, transition: { duration: DUR_APERTO * k, ease: [...CURVA.entrada] } };

  /** Card clicável afunda menos. */
  const apertoCard: TargetAndTransition = reduzido
    ? {}
    : { scale: ESCALA.apertoCard, transition: { duration: DUR_APERTO * k, ease: [...CURVA.entrada] } };

  /**
   * O card na mão: levanta 2%, sem inclinar, num degrau curto (founder 27/09:
   * "o card um pouco pesado pra arrastar"). A inclinação e a mola davam peso
   * ao gesto; agora ele sobe e já está na mão. A sombra de flutuante NÃO vem
   * aqui: `box-shadow` não interpola por variável e repinta a cada quadro. Ela
   * mora numa camada própria (`--vos-sombra-arraste`) que acende por opacidade.
   */
  const levantar: TargetAndTransition = reduzido
    ? {}
    : {
        scale: ESCALA.levanta,
        transition: tempo("xs"),
      };

  /**
   * O respiro do Zé enquanto pensa: 1 → 1.04 → 1, em loop, SÓ enquanto o
   * trabalho corre. Reduzido: parado.
   */
  const respiro: TargetAndTransition = reduzido
    ? { scale: 1 }
    : {
        scale: [1, ESCALA.respiro, 1],
        transition: { duration: CICLO.respiro * Math.max(k, 0.0001), ease: "easeInOut", repeat: Infinity },
      };

  /* ── Estados ───────────────────────────────────────────────────── */

  /**
   * Erro: um tremor DISCRETO (4px, três idas), só quando a pessoa tentou e
   * não deu. Nunca enquanto digita.
   */
  const tremor: { x: number[]; transition: { duration: number; ease: "easeInOut" } } | null = reduzido
    ? null
    : { x: [0, -4, 4, -3, 3, -1, 0], transition: { duration: 0.34 * k, ease: "easeInOut" } };

  /** Destaque de linha que mudou sozinha (tempo real): o fundo acende e apaga. */
  const destaque: TargetAndTransition = {
    backgroundColor: ["var(--vos-destaque)", "var(--vos-destaque-0)"],
    transition: { duration: 1.2 * k, ease: [...CURVA.saida], delay: 0.1 * k },
  };

  /** Momento de sucesso: o disco pousa com quique, o ✓ se desenha, o anel se desfaz. */
  const sucesso = {
    disco: (reduzido
      ? esmaecer()
      : {
          initial: { scale: 0.6, opacity: 0 },
          animate: { scale: 1, opacity: 1, transition: mola("pouso") },
          exit: { opacity: 0, transition: tempo("sm", "saida") },
        }) as Receita,
    traco: {
      initial: { pathLength: reduzido ? 1 : 0 },
      animate: { pathLength: 1, transition: reduzido ? { duration: 0 } : tempo("md", "entrada", 0.12) },
    },
    anel: reduzido
      ? { initial: { opacity: 0 }, animate: { opacity: 0 } }
      : {
          initial: { scale: 1, opacity: 0.35 },
          animate: { scale: 1.7, opacity: 0, transition: tempo("xl", "entrada", 0.08) },
        },
  };

  /** Gráfico na primeira pintura: barra cresce da base em cascata. */
  const barra = (i = 0): Receita =>
    reduzido
      ? esmaecer()
      : {
          initial: { scaleY: 0 },
          animate: {
            scaleY: 1,
            transition: tempo("xl", "entrada", atrasoDaCascata(i, CASCATA.passoGrafico, CASCATA.tetoGrafico)),
          },
          exit: { opacity: 0, transition: tempo("sm", "saida") },
          style: { transformOrigin: "bottom" },
        };

  /** Linha de gráfico se desenha da esquerda pra direita. */
  const traco: Receita = reduzido
    ? { initial: { pathLength: 1, opacity: 0 }, animate: { pathLength: 1, opacity: 1, transition: tempo("xs") }, exit: { opacity: 0 } }
    : {
        initial: { pathLength: 0, opacity: 0 },
        animate: {
          pathLength: 1,
          opacity: 1,
          transition: { pathLength: { duration: DUR.xl * 1.4 * k, ease: [...CURVA.entrada] }, opacity: tempo("xs") },
        },
        exit: { opacity: 0, transition: tempo("sm", "saida") },
      };

  /* Durante a hidratação a receita nasce no lugar (sem `initial`): ver
     `hidratado` em OpcoesDoMovimento. */
  const pronta = (x: Receita): Receita => (hidratado ? x : { ...x, initial: false });
  const prontaPor =
    <A extends unknown[]>(f: (...a: A) => Receita) =>
    (...a: A): Receita =>
      pronta(f(...a));

  return {
    escala: k,
    reduzido,
    hidratado,
    /** Transição de um degrau, pra quem precisa montar a própria. */
    tempo,
    /** Mola nomeada, já escalada. */
    mola,
    /** Tween de um degrau pro item `i` de uma fila (a cascata para no 10º). */
    tempoNaFila: (degrau: Degrau, i: number) => tempo(degrau, "entrada", atrasoDaCascata(i)),
    /** Transição de layout pra quem se reorganiza. `false` quando reduzido. */
    layout: reduzido ? (false as const) : (true as const),
    r: {
      fundo: prontaPor(fundo),
      modal: pronta(modal),
      gaveta: prontaPor(gaveta),
      folha: pronta(folha),
      conteudoProgressivo,
      flutuante: prontaPor(flutuante),
      tooltip: prontaPor(tooltip),
      submenu: pronta(submenu),
      pagina: pronta(pagina),
      vista,
      item: prontaPor(item),
      chip: pronta(chip),
      rotulo: pronta(rotulo),
      icone: prontaPor(icone),
      recolher: pronta(recolher),
      chegada: pronta(chegada),
      mensagem: prontaPor(mensagem),
      toast: pronta(toast),
      aperto,
      apertoCard,
      levantar,
      respiro,
      tremor,
      destaque,
      sucesso: hidratado
        ? sucesso
        : {
            disco: pronta(sucesso.disco),
            traco: { ...sucesso.traco, initial: false as const },
            anel: { ...sucesso.anel, initial: false as const },
          },
      barra: prontaPor(barra),
      traco: pronta(traco),
    },
  };
}

export type Movimento = ReturnType<typeof criarMovimento>;
